import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icon';
import s from './overlay.module.css';

/**
 * Bottom-Sheet (GL-023 §9.10): Weiss, oben radius_xl, Griff, shadow_overlay, Scrim dahinter.
 * Escape und Tipp auf den Scrim schliessen. Fokus geht ins Sheet und beim Schliessen zurueck.
 */
export function Sheet({
  offen,
  onSchliessen,
  titel,
  unterzeile,
  children,
  fuss,
}: {
  offen: boolean;
  onSchliessen: () => void;
  titel: string;
  unterzeile?: string;
  children: ReactNode;
  /** Bleibt unten im Sheet stehen, waehrend der Inhalt scrollt (Mehrfachauswahl: "3 Gerichte hinzufügen") */
  fuss?: ReactNode;
}) {
  const titelId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const vorher = useRef<HTMLElement | null>(null);
  // Aktuellen Schliessen-Handler merken, ohne den Effekt bei jedem Rendern neu zu starten (sonst springt der Fokus)
  const schliessen = useRef(onSchliessen);
  schliessen.current = onSchliessen;

  useEffect(() => {
    if (!offen) return;
    vorher.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const el = ref.current;
    const erstes = el?.querySelector<HTMLElement>('input, button:not([data-griff]), a[href]');
    (erstes ?? el)?.focus();
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        schliessen.current();
      }
      if (e.key === 'Tab' && el) {
        const fokussierbar = Array.from(el.querySelectorAll<HTMLElement>('input, button, a[href], select, textarea')).filter(
          (x) => !x.hasAttribute('disabled'),
        );
        const erstesEl = fokussierbar[0];
        const letztes = fokussierbar[fokussierbar.length - 1];
        if (!erstesEl || !letztes) return;
        if (e.shiftKey && document.activeElement === erstesEl) {
          e.preventDefault();
          letztes.focus();
        } else if (!e.shiftKey && document.activeElement === letztes) {
          e.preventDefault();
          erstesEl.focus();
        }
      }
    };
    document.addEventListener('keydown', taste);
    const ueberlauf = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', taste);
      document.body.style.overflow = ueberlauf;
      vorher.current?.focus();
    };
  }, [offen]);

  if (!offen) return null;
  return createPortal(
    <div className={s.ebene}>
      <div className={s.scrim} onClick={onSchliessen} aria-hidden="true" />
      <div ref={ref} role="dialog" aria-modal="true" aria-labelledby={titelId} className={s.sheet} tabIndex={-1}>
        <span aria-hidden="true" className={s.griff} />
        <div className={s.sheetKopf}>
          <div className={s.sheetTitelBlock}>
            <h2 id={titelId} className={s.sheetTitel}>
              {titel}
            </h2>
            {unterzeile && <p className={s.sheetUnter}>{unterzeile}</p>}
          </div>
          <button type="button" className={s.schliessen} aria-label="Schließen" onClick={onSchliessen}>
            <Icon name="schliessen" groesse={20} strich={2} />
          </button>
        </div>
        <div className={s.sheetInhalt}>{children}</div>
        {fuss && <div className={s.sheetFuss}>{fuss}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Menuezeile im Sheet: 48 px, Icon links, optional destruktiv (Himbeere mit Icon). */
export function MenueZeile({
  icon,
  children,
  destruktiv = false,
  rechts,
  ...rest
}: {
  icon: IconName;
  children: ReactNode;
  destruktiv?: boolean;
  rechts?: ReactNode;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>) {
  return (
    <button type="button" className={`${s.zeile} ${destruktiv ? s.zeileDestruktiv : ''}`} {...rest}>
      <Icon name={icon} groesse={22} />
      <span className={s.zeileText}>{children}</span>
      {rechts}
    </button>
  );
}

/** Ein/Aus-Schalter mit role="switch", 44 px Trefferflaeche. */
export function Schalter({
  an,
  onWechsel,
  label,
  beschreibung,
}: {
  an: boolean;
  onWechsel: (neu: boolean) => void;
  label: string;
  beschreibung?: string;
}) {
  const id = useId();
  return (
    // GL-023 §9.15: ganze Zeile mit Beschriftung klickbar (Tastatur und Screenreader nutzen den Knopf)
    <div className={s.schalterZeile} onClick={() => onWechsel(!an)}>
      <span className={s.schalterText}>
        <span id={`${id}-l`} className={s.schalterLabel}>
          {label}
        </span>
        {beschreibung && (
          <span id={`${id}-b`} className={s.schalterBeschreibung}>
            {beschreibung}
          </span>
        )}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={an}
        aria-labelledby={`${id}-l`}
        aria-describedby={beschreibung ? `${id}-b` : undefined}
        className={s.schalter}
        onClick={(e) => {
          e.stopPropagation();
          onWechsel(!an);
        }}
      >
        <span aria-hidden="true" className={s.schalterDaumen}>
          <Icon name="haken" groesse={14} strich={3} />
        </span>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------- Toast

interface ToastDaten {
  id: number;
  text: string;
  aktion?: { label: string; onClick: () => void };
}

const ToastKontext = createContext<((text: string, aktion?: ToastDaten['aktion']) => void) | null>(null);

/** Toast (GL-023 §9.10): Flaeche ink, Schrift weiss, ueber der Tab-Leiste, 3,5 s. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastDaten | null>(null);
  const zaehler = useRef(0);
  const zeige = useCallback((text: string, aktion?: ToastDaten['aktion']) => {
    zaehler.current += 1;
    setToast({ id: zaehler.current, text, aktion });
  }, []);
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), toast.aktion ? 5000 : 3500);
    return () => window.clearTimeout(t);
  }, [toast]);
  const wert = useMemo(() => zeige, [zeige]);
  return (
    <ToastKontext.Provider value={wert}>
      {children}
      <div className={s.toastBereich} role="status" aria-live="polite">
        {toast && (
          <div key={toast.id} className={s.toast}>
            <span>{toast.text}</span>
            {toast.aktion && (
              <button
                type="button"
                className={s.toastAktion}
                onClick={() => {
                  toast.aktion?.onClick();
                  setToast(null);
                }}
              >
                {toast.aktion.label}
              </button>
            )}
          </div>
        )}
      </div>
    </ToastKontext.Provider>
  );
}

export function useToast(): (text: string, aktion?: { label: string; onClick: () => void }) => void {
  const z = useContext(ToastKontext);
  if (!z) throw new Error('useToast ausserhalb von ToastProvider');
  return z;
}
